import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useCallback, useRef } from "react";
import classes from "./PassageReview.module.css";
import util from "../../styles/utilities.module.css";
import "../../style.css";
import { useSelector, useDispatch } from "react-redux";
import {
  selectFormData,
  setFormData,
  selectWritingPrompt,
  selectTextID,
  selectReadingPassage,
  setWritingPrompt,
  selectAnchorID,
  setReadingPassage,
  setTextID,
  selectTextImages,
  setTextImages,
  selectIncludeImages,
  selectSippsLesson,
  selectSippsLevel
} from "../../slices/form";
import ProgressBarWithCircles from "../Layout/ProgressBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faCommentDots } from "@fortawesome/free-regular-svg-icons"; // Note the change here
import { faRedo } from "@fortawesome/free-solid-svg-icons";

import {
  useAppUsageLogger,
  useFeedbackMutation,
  useGenerateTextMutation,
  useGenerateTextImageMutation,
} from "../../slices/authApi";
import CommentField from "../../common/Comment/CommentField";
import Spinner from "../UI/spinner/spinner";
import ModalWrapper from "../UI/modal_wrapper/modal_wrapper";
import InputModal from "../UI/input_modal/input_modal";
import { Tooltip } from "@mui/material";
import boldTextDetector from "../../utils/boldTextDetector";
import FormattedPassage from "./FormattedPassage";

// This constant must be the *exact and complete* data URI string
// that the backend sends to represent the placeholder image.
// For example: "data:image/png;base64,actual_base64_string_for_grey_1024x1024_jpeg"
const PLACEHOLDER_DATA_URL = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAABAAAAAQAAQMAAABF07nAAAAAA1BMVEWAgICQdD0xAAAAlklEQVR42u3BAQEAAACCIP+vbkhAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADvBgQeAAEN3jhkAAAAAElFTkSuQmCC";
const RegenerateImageInput = React.memo(({ value, onChange, placeholder }) => {
  return (
    <textarea
      rows={3}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
    />
  );
});

function PassageReview() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const strictModeCheckRef = useRef(false); // Ref to handle Strict Mode double invocation

  const [feedback] = useFeedbackMutation();
  const logAppUse = useAppUsageLogger();
  const [generateText, { isLoading: isGenerateTextLoading }] =
    useGenerateTextMutation();
  const [generateTextImage, { isLoading: isGenerateTextImageLoading }] =
    useGenerateTextImageMutation();
  const textID = useSelector(selectTextID);
  const anchorID = useSelector(selectAnchorID);
  const formData = useSelector(selectFormData);
  const writingPrompt = useSelector(selectWritingPrompt);
  const readingPassage = useSelector(selectReadingPassage);
  const textImages = useSelector(selectTextImages);

  const [isEditing, setIsEditing] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState("");
  const [writingPromptPopup, setWritingPromptPopup] = useState(false);
  const [passagePopup, setPassagePopup] = useState(false);
  const [isFeedback, setIsFeedback] = useState(false);
  const [comments, setComments] = useState({ writing_prompt: "", passage: "" });

  const [passage, setPassage] = useState("");
  const [writing_Prompt, setWriting_Prompt] = useState("");

  // popup windows states
  const [showGoBackWindow, setShowGoBackWindow] = useState(false);
  const [showGoForwardWindow, setShowGoForwardWindow] = useState(false);
  const [promptPopup, setPromptPopup] = useState(false);

  const [imagePopup, setImagePopup] = useState(false);
  const { image_instructions, image_speed } = formData || {};
  const includeImages = useSelector(selectIncludeImages);
  const sippsLevel = useSelector(selectSippsLevel);
  const sippsLesson = useSelector(selectSippsLesson);

  const [initialImageLoadDone, setInitialImageLoadDone] = useState(false);

  // feedback endpoint
  const feedbackEndpointHandler = async () => {
    if (isFeedback) {
      try {
        // feedback payload
        const payload = {
          feedback: [
            {
              content_id: textID,
              category: "writing_prompt",
              comments: comments?.writing_prompt,
            },
            {
              content_id: textID,
              category: "passage",
              comments: comments?.passage,
            },
          ],
        };

        const { data } = await feedback(payload);

        if (data?.status === 200) {
          setIsFeedback(false);
          setComments({ writing_prompt: "", passage: "" });
        }
      } catch (err) {
        console.log({ err });
      }
    }
  };

  // regenerate SM endpoint
  const regenerateSMHandler = async (source) => {
    await logAppUse(source, "passage_review");
    try {
      const generateTextData = await generateText({
        anchor_id: anchorID,
        regenerate: true,
        writing_prompt: {
          content: writingPrompt.content,
          edited: writingPrompt.edited,
        },
        sipps_level: sippsLevel,
        sipps_lesson: sippsLesson,
        include_images: false,
      }).unwrap();

      //const images = generateTextData?.images;

      dispatch(
        setReadingPassage(
          generateTextData?.text.trim() || "the returned text key is undefined"
        )
      );
      dispatch(setTextID(generateTextData?.text_id));
      dispatch(setWritingPrompt({ ...writingPrompt, edited: false }));
      //dispatch(setTextImages(images));
    } catch (err) {
      console.log({ err });
    }
  };
  // regenerate image endpoint
  const regenerateImageConfirmHandler = useCallback(async (source) => {
    await logAppUse(source, "passage_review");
    try {
      const generateTextImageData = await generateTextImage({
        text_id: textID,
        image_instructions: image_instructions,
        image_speed: image_speed
      }).unwrap();

      const images = generateTextImageData?.images;

      dispatch(setTextImages(images));
    } catch (err) {
      console.log({ err });
    }
  }, [dispatch, generateTextImage, image_instructions, image_speed, logAppUse, setTextImages, textID]);

  // assessmentCreation SM endpoint
  const assessmentCreationSMHandler = async (source) => {
    await logAppUse(source, "questions_criteria");
    navigate("/questionsCriteria");
  };

  const feedbackSaveHandler = (type) => {
    if (passage !== "" || writing_Prompt !== "") {
      setIsFeedback(true);
    } else {
      setIsFeedback(false);
    }
    if (type === "passage") {
      setComments((preState) => ({ ...preState, [type]: passage }));
      setPassagePopup(false);
    } else {
      setComments((preState) => ({ ...preState, [type]: writing_Prompt }));
      setWritingPromptPopup(false);
    }
  };

  const feedbackCancelHander = (type) => {
    type === "passage" ? setPassagePopup(false) : setWritingPromptPopup(false);
  };

  const regeneratePassageHandler = async () => {
    await logAppUse("passage_review", "use_prompt_pop_up", () =>
      setPromptPopup(true)
    );
  };

  const regenerateImageHandler = async () => {
    await logAppUse("passage_review", "use_regen_image_pop_up", () =>
      setImagePopup(true)
    );
  };

  const forwardBtnContinueWindowHandler = async () => {
    setShowGoForwardWindow(false);
    await assessmentCreationSMHandler("confirm_proceed_pop_up");
  };

  const backBtnRegenerateWindowHandler = async () => {
    setShowGoForwardWindow(false);
    await regenerateSMHandler("use_prompt_pop_up");
  };

  const backBtnConfirmWindowHandler = async () => {
    localStorage.removeItem("from");
    await logAppUse(
      "go_back_pop_up",
      "criteria_selection_reading_comprehension",
      () => {
        setShowGoBackWindow(false);
        navigate("/criteriaselection");
      }
    );
  };

  const backBtnCancelWindowHandler = async () => {
    await logAppUse("go_back_pop_up", "passage_review", () =>
      setShowGoBackWindow(false)
    );
  };

  const promptPopupCancelHandler = async () => {
    await logAppUse("use_prompt_pop_up", "passage_review", () =>
      setPromptPopup(false)
    );
  };

  const promptPopupContinueHandler = async () => {
    setPromptPopup(false);
    await feedbackEndpointHandler();
    regenerateSMHandler("use_prompt_pop_up");
  };

  async function navigateToNextPage() {
    await feedbackEndpointHandler();
    if (writingPrompt?.edited) {
      await logAppUse("passage_review", "confirm_proceed_pop_up");
      setShowGoForwardWindow(true);
    } else {
      await assessmentCreationSMHandler("passage_review");
    }
  }

  async function navigateToPreviousPage() {
    await feedbackEndpointHandler();
    await logAppUse("passage_review", "go_back_pop_up", (data) =>
      setShowGoBackWindow(data?.status === 200)
    );
  }

  useEffect(() => {
    setEditedPrompt(writingPrompt.content);
  }, [writingPrompt.content]);

  useEffect(() => {
    const isPlaceholderPresent = textImages && textImages.length > 0 && textImages[0] === PLACEHOLDER_DATA_URL;
    const conditionsMet = includeImages && isPlaceholderPresent && !initialImageLoadDone && textID;

    if (conditionsMet) {
      if (strictModeCheckRef.current) {
        // If ref is true, this is likely the second call in Strict Mode for these conditions, so skip.
        return;
      }
      
      regenerateImageConfirmHandler("initial_auto_regeneration");
      setInitialImageLoadDone(true); // Primary guard against re-running if successful
      
      // Mark that for this set of conditions, the call has been made (for Strict Mode).
      strictModeCheckRef.current = true;
    } else {
      // If conditions are not met, reset the ref.
      // This allows the effect to run if conditions become met later (and initialImageLoadDone is still false).
      strictModeCheckRef.current = false;
    }
  }, [includeImages, textImages, textID, initialImageLoadDone, regenerateImageConfirmHandler]);

  const getFeedbackIconStyle = (type) => {
    return `${classes.icon} ${
      comments[type] !== "" ? classes["icon-color"] : ""
    }`;
  };

  return (
    <>
      <div className={classes.pageMargins}>
        {isGenerateTextLoading ? (
          <Spinner
            message={
              "We are hard at work creating a new passage. This may take a few minutes"
            }
          />
        ) : null}
        <p style={{ fontSize: "larger", textAlign: "center" }}>
          <strong style={{ fontSize: "inherit" }}>Passage Review</strong>
          <br />
          Grade: {formData.grade}, Difficulty: {formData.difficulty_level}
        </p>
        <div style={{ paddingTop: "60px" }} />

        <ProgressBarWithCircles progress={50} />

        <div className={classes["container"]}>
          <h2 className={classes.title}>Writing prompt:</h2>

          <div className={classes["content-wrapper"]}>
            <div
              className={`${classes["paragraph-box"]} ${
                isEditing ? classes["editing"] : ""
              }`}
            >
              {isEditing && (
                <textarea
                  className={classes["editable-text"]}
                  value={editedPrompt}
                  onChange={(e) => setEditedPrompt(e.target.value)}
                />
              )}
              {!isEditing ? (
                <div className={classes.writingPromptFlex}>
                  <p>{boldTextDetector(writingPrompt.content)}</p>
                  <Tooltip title="Edit the writing prompt. Then, click on Regenerate Passage to generate a new passage based on your revised prompt.">
                    <FontAwesomeIcon
                      className={`${classes.icon} ${
                        writingPrompt?.edited ? classes["icon-color"] : ""
                      }`}
                      icon={faEdit}
                      onClick={() => {
                        setIsEditing(true);
                      }}
                    />
                  </Tooltip>
                </div>
              ) : null}
              {writingPromptPopup && (
                <CommentField
                  valueHandler={(comment) => setWriting_Prompt(comment)}
                  cancelHandler={() => feedbackCancelHander("writing_prompt")}
                  saveHandler={() => feedbackSaveHandler("writing_prompt")}
                  comment={writing_Prompt}
                  placeholder={
                    "share your comments about the writing prompt here"
                  }
                />
              )}
              {isEditing && (
                <div className={`${classes["icon-container"]} ${classes.writingPromptActions}`}>
                  <button
                    type="button"
                    className={`${util.btn} ${util['btn--ghost']} ${util['btn-sm']}`}
                    onClick={() => {
                      setEditedPrompt(writingPrompt.content);
                      setIsEditing(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`${util.btn} ${util['btn-sm']}`}
                    onClick={() => {
                      dispatch(
                        setWritingPrompt({
                          content: editedPrompt,
                          edited: true,
                        })
                      );
                      setIsEditing(false);
                    }}
                  >
                    Update
                  </button>
                </div>
              )}
              {!isEditing && !writingPromptPopup && (
                <div className={classes["icon-container"]}>
                  <FontAwesomeIcon
                    className={getFeedbackIconStyle("writing_prompt")}
                    icon={faCommentDots}
                    onClick={() => {
                      setWritingPromptPopup(true);
                      setWriting_Prompt(comments?.writing_prompt);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ paddingTop: "30px" }} />

        <div className={classes["container"]}>
          <h2 className={classes.title}>
            <span style={{ marginRight: "6px" }}>Passage:</span>
            <span>({formData.genre})</span>
          </h2>

          <div className={classes["content-wrapper"]}>
            <div className={classes["paragraph-box"]}>
              <FormattedPassage
                text={readingPassage}
                images={textImages}
                boldTextDetector={boldTextDetector}
                newImageLoading={isGenerateTextImageLoading}
              />
              {passagePopup ? (
                <CommentField
                  valueHandler={(comment) => setPassage(comment)}
                  cancelHandler={() => feedbackCancelHander("passage")}
                  saveHandler={() => feedbackSaveHandler("passage")}
                  comment={passage}
                  placeholder={"share your comments about the passage here"}
                />
              ) : (
                <div className={classes["icon-container"]}>
                  <FontAwesomeIcon
                    className={getFeedbackIconStyle("passage")}
                    icon={faCommentDots}
                    onClick={() => {
                      setPassagePopup(true);
                      setPassage(comments?.passage);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={classes["text-button-container"]}>
          {includeImages && (
            <button
              className={`${util.btn} ${util['btn--ghost']} ${util['btn-sm']} ${util['push-left']}`}
              onClick={regenerateImageHandler}
            >
              <FontAwesomeIcon className={classes.btnIcon} icon={faRedo} />
              Regenerate Image
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button
            className={`${util.btn} ${util['btn--ghost']} ${util['btn-sm']} ${util['push-right']}`}
            onClick={regeneratePassageHandler}
          >
            <FontAwesomeIcon className={classes.btnIcon} icon={faRedo} />
            Regenerate Text
          </button>
        </div>

        <div className={classes.navigationButtonsContainer}>
          <div className={`${classes['form-actions']} ${classes['form-actions--flow']}`}>
            <button
              className={`${util.btn} ${util['btn--ghost']} ${classes.navButtonWide}`}
              onClick={navigateToPreviousPage}
            >
              Back
            </button>
            <button
              className={`${util.btn} ${util['btn--secondary']} ${classes.navButtonWide}`}
              onClick={navigateToNextPage}
            >
              Continue
            </button>
          </div>
        </div>
      </div>

      {/* Back Arrow Modal */}
      <ModalWrapper
        modalProps={{
          isOpen: showGoBackWindow,
          description: `Are you sure you want to leave this page ? Your current work will be lost.`,
          leftBtnHandler: backBtnCancelWindowHandler,
          rightBtnHandler: backBtnConfirmWindowHandler,
          buttons: {
            leftBtn: "Cancel",
            rightBtn: "Confirm",
          },
        }}
      />

      {/* Regenerate Passage Modal */}
      <ModalWrapper
        modalProps={{
          isOpen: promptPopup,
          description: `We will regenerate the passage using the displayed writing prompt. If you want to edit the prompt,
          press cancel to edit before continuing.`,
          leftBtnHandler: promptPopupCancelHandler,
          rightBtnHandler: promptPopupContinueHandler,
          buttons: {
            leftBtn: "Cancel",
            rightBtn: "Continue",
            isToggleStyle: true,
          },
        }}
      />

      {/* Regenerate image modal (prompt to optionally change image steering instructions) */}
      <InputModal
        modalProps={{
          isOpen: imagePopup,
          description: `Press continue to regenerate the image. You may optionally modify the image instructions first.`,
          leftBtnHandler: () => {
            setImagePopup(false);
          },
          rightBtnHandler: () => {
            setImagePopup(false);
            regenerateImageConfirmHandler("use_image_regen_pop_up");            
          },
          buttons: {
            leftBtn: "Cancel",
            rightBtn: "Continue",
          },
        }}
        InputComponent={RegenerateImageInput}
        inputProps={{
            value: image_instructions,
            onChange: (e) => dispatch(setFormData({ image_instructions: e.target.value })),
            placeholder: "Describe the appearance of the characters, setting, etc.",
        }}        
      />

      {/* Forward Arrow Modal */}
      <ModalWrapper
        modalProps={{
          isOpen: showGoForwardWindow,
          description: ` Proceeding will discard your edits to the writing prompt. To apply your changes, press 'Regenerate' to
          create a new passage based on your edited prompt.`,
          leftBtnHandler: backBtnRegenerateWindowHandler,
          rightBtnHandler: forwardBtnContinueWindowHandler,
          buttons: {
            leftBtn: "Regenerate",
            rightBtn: "Continue",
            isToggleStyle: true,
          },
        }}
      />
    </>
  );
}

export default PassageReview;
